import { useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import {
  getDashboardRegistrationtrends,
  getDashboardVerificationtrends,
  getUserCurrentTrends,
  getTotalActiveUsersData,
  getEngagementRatesData,
  getDashboardStatsData,
  getDashboardDataPractiseAdmin
} from '../services/dashboard';

import { DEFAULT_RANGES, getRangeTypeFromTimeRange, ROLES, TIME_RANGE } from '../constants';
import { TimeRange } from '../data';

/* ---------------- Helpers ---------------- */

const formatDate = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const subtractDays = (date: Date, days: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() - days);
  return d;
};

const subtractMonths = (date: Date, months: number) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() - months);
  return d;
};

const getDateRange = (rangeType: string) => {
  const today = new Date();

  switch (rangeType) {
    case 'today':
      return { startDate: formatDate(today), endDate: formatDate(today) };

    case TIME_RANGE.WEEKLY:
      return {
        startDate: formatDate(subtractDays(today, DEFAULT_RANGES.WEEKLY_DAYS)),
        endDate: formatDate(today)
      };
    case TIME_RANGE.MONTHLY:
      return {
        startDate: formatDate(subtractDays(today, 30)),
        endDate: formatDate(today)
      };
    case TIME_RANGE.YEARLY:
    default:
      return {
        startDate: formatDate(subtractDays(today, DEFAULT_RANGES.YEARLY_DAYS)),
        endDate: formatDate(today)
      };
  }
};

/* ---------------- Hook ---------------- */

export const useDashboardData = () => {
  const [selectedValueRegistration, setSelectedValueRegistration] = useState<TimeRange>(
    TIME_RANGE.WEEKLY
  );

  const [selectedValueVerification, setSelectedValueVerification] = useState<TimeRange>(
    TIME_RANGE.YEARLY
  );

  const [selectedValueCurrentTrends, setSelectedValueCurrentTrends] = useState<TimeRange>(
    TIME_RANGE.YEARLY
  );

  const [selectedValueActiveUsers, setSelectedValueActiveUsers] = useState<TimeRange>(
    TIME_RANGE.YEARLY
  );

  const [selectedValueEngagementRates, setSelectedValueEngagementRates] = useState<TimeRange>(
    TIME_RANGE.WEEKLY
  );

  /* ---------------- Queries ---------------- */

  const userRole = localStorage.getItem('userRole');
  const organisationId = localStorage.getItem('organisationId') || '';

  const statsQuery = useQuery({
    queryKey: ['dashboardStats', userRole],
    queryFn: async () => {
      const today = new Date();

      const basePayload = {
        startDateActive: formatDate(subtractDays(today, DEFAULT_RANGES.ACTIVE_DAYS)),
        endDateActive: formatDate(today),
        patientActiveStartDate: formatDate(subtractMonths(today, DEFAULT_RANGES.ACTIVE_MONTHS)),
        patientActiveEndDate: formatDate(today)
      };

      //  SUPER ADMIN
      if (userRole === ROLES.SUPER_ADMIN) {
        return getDashboardStatsData(basePayload);
      }

      //  PRACTISE ADMIN
      if (userRole === ROLES.PRACTISE_ADMIN) {
        if (!organisationId) {
          throw new Error('Organisation ID not found');
        }

        return getDashboardDataPractiseAdmin({
          ...basePayload,
          organisationId
        });
      }

      // Optional fallback
      return null;
    }
  });

  const registrationQuery = useQuery({
    queryKey: ['registrationTrends', selectedValueRegistration],
    queryFn: () =>
      getDashboardRegistrationtrends({
        rangeType: getRangeTypeFromTimeRange(selectedValueRegistration)
      })
  });

  const verificationQuery = useQuery({
    queryKey: ['verificationTrends', selectedValueVerification],
    queryFn: () =>
      getDashboardVerificationtrends({
        rangeType: getRangeTypeFromTimeRange(selectedValueVerification)
      })
  });

  const currentTrendsQuery = useQuery({
    queryKey: ['currentTrends', selectedValueCurrentTrends],
    queryFn: () =>
      getUserCurrentTrends({
        rangeType: getRangeTypeFromTimeRange(selectedValueCurrentTrends)
      })
  });

  const activeUsersQuery = useQuery({
    queryKey: ['activeUsers', selectedValueActiveUsers],
    queryFn: () => {
      const { startDate, endDate } = getDateRange(selectedValueActiveUsers);
      return getTotalActiveUsersData({ startDate, endDate });
    }
  });

  const engagementRatesQuery = useQuery({
    queryKey: ['engagementRates', selectedValueEngagementRates],
    queryFn: () =>
      getEngagementRatesData({
        rangeType: getRangeTypeFromTimeRange(selectedValueEngagementRates)
      })
  });

  /* ---------------- Handlers ---------------- */

  const createChangeHandler =
    (setter: React.Dispatch<React.SetStateAction<TimeRange>>) => (event: SelectChangeEvent) =>
      setter(event.target.value as TimeRange);

  return {
    registrationData: registrationQuery.data,
    verificationData: verificationQuery.data,
    currentTrendsData: currentTrendsQuery.data,
    engagementRatesData: engagementRatesQuery.data,
    statsData: statsQuery.data,
    activeUsersData: activeUsersQuery.data,

    loading: false,

    error: statsQuery.error || registrationQuery.error || verificationQuery.error,

    selectedValueRegistration,
    selectedValueVerification,
    selectedValueCurrentTrends,
    selectedValueActiveUsers,
    selectedValueEngagementRates,

    handleChangeRegistration: createChangeHandler(setSelectedValueRegistration),
    handleChangeVerification: createChangeHandler(setSelectedValueVerification),
    handleChangeCurrentTrends: createChangeHandler(setSelectedValueCurrentTrends),
    handleChangeActiveUsersTrends: createChangeHandler(setSelectedValueActiveUsers),
    handleChangeEngagementRates: createChangeHandler(setSelectedValueEngagementRates)
  };
};
